import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// The map in a Timesheets day pop-up (Phelipe 2026-10-27: "if we click on
// something like a breadcrumb location ... show the info on the right side").
// Each job's pin and area, the breadcrumb stops in order (numbered, joined by
// a dashed line) and where the phone was at every clock-in and clock-out.
// The timesheets controller says what was clicked (timesheet-day-map:pick);
// a click on a stop or a clock pin here says it back (timesheet-day-map:picked).
// A drive (Kloepfer K3-100) turns its two stops into a green A (where it left)
// and a red B (where it got to), joined in blue: a straight line, since the
// phone keeps the stops and not the road between.
// Esri satellite photos, keyless (Phelipe 2026-10-28: stops read better on the real ground).
const BASE = "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
const LABELS = "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
// A plain street map by default, satellite from the switch in the corner (Phelipe 2026-10-29); the pick is remembered.
const STREETS = "https://services.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
const STYLE_KEY = "timesheets.dayMapStyle"
const AREA = { color: "#2563eb", weight: 1.5, dashArray: "5 4", fillColor: "#3b82f6", fillOpacity: 0.08 }
const CLOSE_ZOOM = 16

export default class extends Controller {
  static values = { data: Object, text: Object }

  connect() {
    const data = this.dataValue || {}
    this.map = L.map(this.element, { attributionControl: false, scrollWheelZoom: true })
    this.layers = {
      satellite: [ L.tileLayer(BASE, { maxZoom: 19, maxNativeZoom: 18 }), L.tileLayer(LABELS, { maxZoom: 19, maxNativeZoom: 16, pane: "overlayPane" }) ],
      map: [ L.tileLayer(STREETS, { maxZoom: 19, maxNativeZoom: 18 }) ]
    }
    this.addStyleSwitch()
    this.setStyle(this.savedStyle())
    this.points = []

    ;(data.jobs || []).forEach(job => {
      const area = Array.isArray(job.shape) && job.shape.length >= 3
        ? L.polygon(job.shape, AREA)
        : L.circle([job.lat, job.lng], { ...AREA, radius: job.radius_m || 100 })
      area.addTo(this.map).bindTooltip(job.name || "")
      L.circleMarker([job.lat, job.lng], { radius: 4, color: "#1d4ed8", fillColor: "#1d4ed8", fillOpacity: 1, weight: 1 }).addTo(this.map)
      this.points.push([job.lat, job.lng])
    })

    const stops = (data.stops || []).map((stop, i) => ({ ...stop, i })).filter(stop => stop.lat != null && stop.lng != null)
    if (stops.length > 1) {
      L.polyline(stops.map(stop => [stop.lat, stop.lng]), { color: "#374151", weight: 2.5, opacity: 0.7, dashArray: "6 6" }).addTo(this.map)
    }
    this.drives = data.drives || []
    this.stops = {}
    stops.forEach(stop => {
      const marker = L.marker([stop.lat, stop.lng], { icon: this.stopIcon(stop.i + 1, false), title: stop.name || "", zIndexOffset: 200 })
      marker.on("click", () => this.say(`stop:${stop.i}`))
      marker.addTo(this.map)
      this.stops[stop.i] = marker
      this.points.push([stop.lat, stop.lng])
    })

    this.punches = (data.punches || []).filter(p => p.lat != null && p.lng != null).map(p => {
      const marker = L.marker([p.lat, p.lng], { icon: this.punchIcon(p), zIndexOffset: 500 })
      marker.on("click", () => this.say(`clock:${p.entry}:${p.kind}`))
      marker.addTo(this.map)
      this.points.push([p.lat, p.lng])
      return { marker, p }
    })

    this.fit(this.points)
    // The pop-up has just been laid out: measure again once it has its size.
    this.frame = requestAnimationFrame(() => {
      if (!this.map) return
      this.map.invalidateSize()
      this.fit(this.points)
    })
  }

  savedStyle() {
    try {
      return window.localStorage.getItem(STYLE_KEY) === "satellite" ? "satellite" : "map"
    } catch (_e) {
      return "map"
    }
  }

  setStyle(style) {
    Object.entries(this.layers).forEach(([name, list]) => list.forEach(layer => {
      if (name === style) layer.addTo(this.map)
      else layer.remove()
    }))
    this.style = style
    this.switchButtons?.forEach(button => {
      const on = button.dataset.style === style
      button.setAttribute("aria-pressed", String(on))
      button.style.background = on ? "#111827" : "#ffffff"
      button.style.color = on ? "#ffffff" : "#374151"
    })
  }

  // Satellite | Map, in the map's bottom-right corner.
  addStyleSwitch() {
    const text = this.textValue || {}
    const SwitchControl = L.Control.extend({
      onAdd: () => {
        const box = L.DomUtil.create("div", "leaflet-bar")
        box.style.display = "flex"
        box.style.overflow = "hidden"
        box.setAttribute("role", "group")
        this.switchButtons = [ [ "satellite", text.satellite || "Satellite" ], [ "map", text.map || "Map" ] ].map(([style, label]) => {
          const button = L.DomUtil.create("button", "", box)
          button.type = "button"
          button.dataset.style = style
          button.textContent = label
          Object.assign(button.style, { font: "600 13px Inter, system-ui, sans-serif", padding: "6px 10px", border: "0", cursor: "pointer" })
          L.DomEvent.on(button, "click", event => {
            L.DomEvent.stop(event)
            this.setStyle(style)
            try { window.localStorage.setItem(STYLE_KEY, style) } catch (_e) { /* private window */ }
          })
          return button
        })
        L.DomEvent.disableClickPropagation(box)
        return box
      }
    })
    new SwitchControl({ position: "bottomright" }).addTo(this.map)
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
    this.map?.remove()
    this.map = null
  }

  // What the left side or a card asked to show: a stop, a time entry's
  // clock-in and clock-out, one of them, or the whole day.
  pick(event) {
    if (!this.map) return
    this.map.invalidateSize()
    const [kind, a, b] = String(event.detail?.sel || "overview").split(":")
    const drive = kind === "drive" ? this.drives[Number(a)] : null
    Object.entries(this.stops).forEach(([i, marker]) => {
      const end = drive ? (Number(i) === drive.a ? "from" : Number(i) === drive.b ? "to" : null) : null
      marker.setIcon(end ? this.endIcon(end) : this.stopIcon(Number(i) + 1, kind === "stop" && a === i))
      marker.setOpacity(drive && !end ? 0.45 : 1)
      marker.setZIndexOffset(end ? 800 : 200)
    })
    this.punches.forEach(({ marker, p }) => {
      const mine = kind === "entry" || kind === "clock" ? String(p.entry) === a : true
      marker.setOpacity(kind === "stop" || drive ? 0.35 : mine ? 1 : 0.25)
      marker.setZIndexOffset(kind === "clock" && mine && p.kind === b ? 1000 : 500)
    })
    this.driveLine?.remove()
    this.driveLine = null
    if (drive && this.stops[drive.a] && this.stops[drive.b]) {
      const ends = [ this.stops[drive.a].getLatLng(), this.stops[drive.b].getLatLng() ]
      this.driveLine = L.polyline(ends, { color: "#2563eb", weight: 5, opacity: 0.9, dashArray: "10 8" }).addTo(this.map)
      return this.fit(ends)
    }
    if (kind === "stop" && this.stops[a]) return this.closeTo(this.stops[a].getLatLng())
    if (kind === "clock") {
      const hit = this.punches.find(({ p }) => String(p.entry) === a && p.kind === b)
      if (hit) return this.closeTo(hit.marker.getLatLng())
    }
    if (kind === "entry") {
      const mine = this.punches.filter(({ p }) => String(p.entry) === a).map(({ marker }) => marker.getLatLng())
      if (mine.length) return this.fit(mine)
    }
    this.fit(this.points)
  }

  say(sel) {
    this.dispatch("picked", { detail: { sel } })
  }

  closeTo(latLng) {
    this.map.setView(latLng, Math.max(this.map.getZoom() || 0, CLOSE_ZOOM))
  }

  fit(points) {
    if (!this.map || !points.length) return
    if (points.length === 1) return this.map.setView(points[0], CLOSE_ZOOM)
    this.map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 17 })
  }

  stopIcon(number, on) {
    const ring = on ? "var(--color-brand, #e4572e)" : "#374151"
    return L.divIcon({
      className: "",
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      html: `<span style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;` +
        `border:2px solid ${ring};background:${on ? ring : "#fff"};color:${on ? "#fff" : "#111827"};` +
        `font:700 13px/1 system-ui,sans-serif;box-shadow:0 1px 3px rgba(0,0,0,.3);cursor:pointer">${number}</span>`
    })
  }

  // A drive's ends on the map: a green A where it left, a red B where it got
  // to (Phelipe 2026-11-02: letters on the map, cars on the card).
  endIcon(end) {
    const [letter, color] = end === "from" ? [ "A", "#16a34a" ] : [ "B", "#dc2626" ]
    return L.divIcon({
      className: "",
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      html: `<span data-drive-end="${end}" style="display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:9999px;` +
        `border:2px solid #fff;background:${color};color:#fff;font:700 14px/1 system-ui,sans-serif;` +
        `box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:pointer">${letter}</span>`
    })
  }

  // A small flag on a pole: green for a clock-in (to the left), dark gray for
  // a clock-out (to the right), with the time in the viewer's own zone.
  punchIcon(p) {
    const text = this.textValue || {}
    const at = p.at ? new Date(p.at) : null
    const time = at && !Number.isNaN(at.getTime()) ? at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""
    const label = `${p.kind === "in" ? (text.in || "In") : (text.out || "Out")} ${time}`.trim()
    const color = p.kind === "in" ? "#15803d" : "#374151"
    const side = p.kind === "in" ? "right:-1px;align-items:flex-end" : "left:-1px;align-items:flex-start"
    return L.divIcon({
      className: "",
      iconSize: [0, 0],
      iconAnchor: [0, 0],
      html: `<span style="position:absolute;bottom:0;${side};display:flex;flex-direction:column;cursor:pointer">` +
        `<span style="white-space:nowrap;background:${color};color:#fff;font:600 13px/1 system-ui,sans-serif;padding:4px 7px;border-radius:5px;box-shadow:0 1px 3px rgba(0,0,0,.3)">${this.escape(label)}</span>` +
        `<span style="width:2px;height:16px;background:${color}"></span></span>`
    })
  }

  escape(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
